import React, { useState, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import { MessageCircle, Upload, Loader2, AlertCircle } from 'lucide-react';
import { useCredits } from '../contexts/CreditContext';
import { getApiUrl } from '../config';

const CustomerSalesCallSimulator = memo(({ onStartConversation }) => {
  const { updateCreditsFromResponse } = useCredits();
  const [files, setFiles] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState('ceo');
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState('');

  // Customer personas configuration - memoized to prevent recreation
  const customerPersonas = useMemo(() => [
    {
      id: 'ceo',
      name: 'CEO',
      description: 'High-level decision maker focused on business impact, ROI, and strategic alignment',
      icon: '👔',
      characteristics: 'Interested in business value, cost-benefit analysis, and strategic fit'
    },
    {
      id: 'cto',
      name: 'CTO',
      description: 'Technical leader focused on architecture, scalability, and technology strategy',
      icon: '⚙️',
      characteristics: 'Asks about technical architecture, scalability, and integration capabilities'
    },
    {
      id: 'engineer',
      name: 'Software Engineer',
      description: 'Technical implementer focused on code quality, security, and technical details',
      icon: '💻',
      characteristics: 'Deep technical questions about implementation, security, and performance'
    },
    {
      id: 'manager',
      name: 'Engineering Manager',
      description: 'Team leader focused on productivity, team impact, and operational efficiency',
      icon: '👥',
      characteristics: 'Concerned with team productivity, maintenance, and operational overhead'
    },
    {
      id: 'procurement',
      name: 'Procurement Manager',
      description: 'Cost-focused decision maker concerned with pricing, contracts, and vendor management',
      icon: '💰',
      characteristics: 'Focuses on pricing, contract terms, and vendor evaluation criteria'
    },
    {
      id: 'car_buyer',
      name: 'Car Buyer',
      description: 'Average car buyer interested in features, pricing, financing, and vehicle comparison',
      icon: '🚗',
      characteristics: 'Asks about features, pricing, financing options, and compares different vehicles'
    }
  ], []);

  const handleFileChange = useCallback((e) => {
    setFiles(Array.from(e.target.files));
    setError('');
  }, []);

  const initializeChat = useCallback(async (e) => {
    e.preventDefault();
    if (!files.length) {
      setError('Please upload at least one file.');
      return;
    }
    
    setIsInitializing(true);
    setError('');
    
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      formData.append('persona', selectedPersona);
      
      const response = await axios.post(getApiUrl('/initialize_sales_call'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      
      // Create initial message
      const initialMessage = {
        id: 1,
        role: 'customer',
        content: response.data.initial_message,
        timestamp: new Date()
      };
      
      // Prepare conversation data
      const conversationData = {
        messages: [initialMessage],
        selectedPersona,
        customerPersonas,
        files
      };
      
      // Navigate to conversation page
      onStartConversation(conversationData);
      
      // Update credits from response
      updateCreditsFromResponse(response.data);
      
      // Show credit consumption info if available (only in development)
      if (process.env.NODE_ENV === 'development' && response.data.credits_used && response.data.remaining_credits !== undefined) {
        console.log(`Sales call started! Used ${response.data.credits_used} credit. Remaining credits: ${response.data.remaining_credits}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize sales call. Please try again.');
    } finally {
      setIsInitializing(false);
    }
  }, [files, selectedPersona, customerPersonas, onStartConversation, updateCreditsFromResponse]);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Sales Call Simulator</h1>
        <p className="text-gray-600 mb-6">
          Practice sales conversations with AI-powered customers. Upload your product materials and start a realistic sales call simulation.
        </p>
        
        {/* File Upload Section */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">1. Upload Product Materials</h2>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
              accept=".pdf,.doc,.docx,.txt,.md"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <span className="text-blue-600 hover:text-blue-700 font-medium">
                Choose files
              </span>
              <span className="text-gray-500"> or drag and drop</span>
            </label>
            <p className="text-sm text-gray-500 mt-2">
              PDF, DOC, DOCX, TXT, MD files accepted
            </p>
            {files.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-700">Selected files:</p>
                <ul className="mt-2 space-y-1">
                  {files.map((file, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      {file.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Persona Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">2. Select Customer Persona</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customerPersonas.map((persona) => (
              <div
                key={persona.id}
                onClick={() => setSelectedPersona(persona.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedPersona === persona.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">{persona.icon}</span>
                  <h3 className="font-semibold text-gray-800">{persona.name}</h3>
                </div>
                <p className="text-sm text-gray-600 mb-2">{persona.description}</p>
                <p className="text-xs text-gray-500">{persona.characteristics}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Initialize Chat Button */}
        <div className="mb-8">
          <button
            onClick={initializeChat}
            disabled={!files.length || isInitializing}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isInitializing ? (
              <>
                <Loader2 className="inline w-5 h-5 mr-2 animate-spin" />
                Initializing Sales Call...
              </>
            ) : (
              <>
                <MessageCircle className="inline w-5 h-5 mr-2" />
                Start Sales Call
              </>
            )}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

CustomerSalesCallSimulator.displayName = 'CustomerSalesCallSimulator';

export default CustomerSalesCallSimulator; 